if (!window.customElements.get('product-card')) {
  const PRODUCT_QUANTITY_SYNC_EVENT = 'recharge:product-quantity-sync';
  const PRODUCT_CARD_LOADING_EVENT = 'recharge:product-card-loading';
  const PRODUCT_CARD_QTY_CACHE_KEY = 'recharge-product-card-qty-cache';
  const PRODUCT_CARD_QTY_CACHE_TTL = 5 * 60 * 1000;
  const RECHARGE_ERROR_AUTO_HIDE_MS = 5000;
  const activeRechargeCards = new Set();

  class ProductCard extends HTMLElement {
    constructor() {
      super();
      this._domEventsController = null;
      this.quantityBadge = this.querySelector('[data-small-product-card-quantity-badge]');
      this.quantitySelectorWrappers = [...this.querySelectorAll('[data-small-product-card-quantity-selector]')];
      this.quantitySelectorInputs = [...this.querySelectorAll('[data-quantity-input]')];
      this.addToBoxButtons = [...this.querySelectorAll('[data-product-card-add-to-box]')];
      this.quantitySelectorInput = this.quantitySelectorInputs[0] || null;
      this.addToBoxButton = this.addToBoxButtons[0] || null;
      this.variantId = this.addToBoxButton?.getAttribute('data-variant-id');
      this.productId = this.addToBoxButton?.getAttribute('data-product-id');
      this.type = this.addToBoxButton?.getAttribute('data-type');
      this.isRechargePending = this.getAttribute('data-recharge-pending') === 'true';
      this.expirationDate = this.getAttribute('data-expiration-date');
      this.ctaWrappers = this.querySelectorAll('[data-product-card-cta]');
      this.controls = this.querySelectorAll('[data-product-card-controls]');
      this.changeBillDateToggles = this.querySelectorAll('[data-product-card-bill-date-toggle]');

      const parsedMaxQuantity = parseInt(this.getAttribute('data-max-quantity'), 10);
      this.maxQuantity = Number.isFinite(parsedMaxQuantity) && parsedMaxQuantity > 0 ? parsedMaxQuantity : Infinity;

      this.currentQuantity = 0;
      this.desiredQuantity = 0;
      this.lastConfirmedQuantity = 0;
      this.itemId = null;
      this.isLoading = false;
      this.isGloballyLocked = false;
      this.isPersistingQuantity = false;
      this.persistTimeout = null;
      this.rechargeErrorElement = null;
      this.rechargeErrorTimeout = null;
    }

    get shouldUseRechargeFlow() {
      return this.type === 'add-on' || this.type === 'deal';
    }

    get rechargeApi() {
      return window.ButcherBoxRechargeProductCardApi;
    }

    getCacheKey(type, variantId) {
      return `${type}:${variantId}`;
    }

    getCachedProductQuantityFromStorage() {
      if (!this.type || !this.variantId) return null;

      try {
        const raw = sessionStorage.getItem(PRODUCT_CARD_QTY_CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        const entry = parsed?.[this.getCacheKey(this.type, this.variantId)];
        if (!entry) return null;

        const isExpired = Date.now() - (entry.updatedAt || 0) > PRODUCT_CARD_QTY_CACHE_TTL;
        if (isExpired) return null;

        return {
          quantity: Number(entry.quantity) || 0,
          id: entry.id || null,
        };
      } catch {
        return null;
      }
    }

    writeCachedProductQuantityToStorage(quantity, id = this.itemId) {
      if (!this.type || !this.variantId) return;

      try {
        const raw = sessionStorage.getItem(PRODUCT_CARD_QTY_CACHE_KEY);
        const parsed = raw ? JSON.parse(raw) : {};
        const cache = parsed && typeof parsed === 'object' ? parsed : {};
        const key = this.getCacheKey(this.type, this.variantId);
        const parsedQuantity = Math.max(0, Number(quantity) || 0);

        if (parsedQuantity <= 0) {
          delete cache[key];
        } else {
          cache[key] = {
            quantity: parsedQuantity,
            id: id || null,
            updatedAt: Date.now(),
          };
        }

        sessionStorage.setItem(PRODUCT_CARD_QTY_CACHE_KEY, JSON.stringify(cache));
      } catch {
        // Ignore storage write errors.
      }
    }

    isSameQuantitySnapshot(localEntry, remoteEntry) {
      const localQuantity = Number(localEntry?.quantity) || 0;
      const remoteQuantity = Number(remoteEntry?.quantity) || 0;
      const localId = localEntry?.id ? String(localEntry.id) : null;
      const remoteId = remoteEntry?.id ? String(remoteEntry.id) : null;

      return localQuantity === remoteQuantity && localId === remoteId;
    }

    async waitForRechargeApi() {
      if (this.rechargeApi) return this.rechargeApi;

      const maxAttempts = 20;
      let attempts = 0;

      while (!window.ButcherBoxRechargeProductCardApi && attempts < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        attempts += 1;
      }

      return window.ButcherBoxRechargeProductCardApi || null;
    }

    connectedCallback() {
      this._domEventsController?.abort();
      this._domEventsController = new AbortController();
      const { signal } = this._domEventsController;

      if (this.addToBoxButtons.length && this.quantitySelectorWrappers.length && this.quantitySelectorInputs.length) {
        this.addToBoxButtons.forEach((button) => {
          button.addEventListener('click', () => this.handleAddToBoxClick(), { signal });
        });

        this.quantitySelectorInputs.forEach((input) => {
          input.addEventListener('minusOnZero', () => this.handleRemoveFromBox(), { signal });
          input.addEventListener('change', (event) => this.handleQuantityChange(event), { signal });
        });
      }

      if (this.shouldUseRechargeFlow && this.variantId && this.productId) {
        const cachedQuantity =
          this.getCachedProductQuantityFromStorage() || this.rechargeApi?.getCachedProductQuantity?.(this.type, this.variantId);
        if (cachedQuantity) {
          this.currentQuantity = cachedQuantity.quantity || 0;
          this.desiredQuantity = this.currentQuantity;
          this.lastConfirmedQuantity = this.currentQuantity;
          this.itemId = cachedQuantity.id || null;
          this.applyUiState();
        }

        this.syncInitialState();
      }

      this._productQuantitySyncHandler = (event) => this.handleProductQuantitySync(event);
      document.addEventListener(PRODUCT_QUANTITY_SYNC_EVENT, this._productQuantitySyncHandler);

      this._productCardLoadingHandler = (event) => this.handleProductCardLoading(event);
      document.addEventListener(PRODUCT_CARD_LOADING_EVENT, this._productCardLoadingHandler);

      this.detailsModal = this.querySelector('[data-product-details-modal]');
      if (this.detailsModal) {
        this.querySelectorAll('view-details-button').forEach((btn) => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.openDetailsModal(btn);
          });
        });

        this.detailsModal
          .querySelector('[data-product-details-modal-overlay]')
          ?.addEventListener('click', () => this.closeDetailsModal(), { signal });
        this.detailsModal
          .querySelector('[data-product-details-modal-close]')
          ?.addEventListener('click', () => this.closeDetailsModal(), { signal });

        this._escapeHandler = (e) => {
          if (e.key === 'Escape' && this.detailsModal.classList.contains('is-open')) {
            this.closeDetailsModal();
          }
        };

        document.addEventListener('keydown', this._escapeHandler, { signal });

        document.body.appendChild(this.detailsModal);
      }

      if (this.expirationDate && this.ctaWrappers.length >= 1) {
        this.ctaWrappers.forEach((wrapper) => wrapper.classList.add('opacity-50', 'pointer-events-none'));

        if (window.anchorDate && window.portalLink) {
          this.validateExpiration();
        }

        document.addEventListener('setRechargeVariables', () => this.validateExpiration());
      }
    }

    disconnectedCallback() {
      this._domEventsController?.abort();

      if (this._escapeHandler) {
        document.removeEventListener('keydown', this._escapeHandler);
      }

      if (this.persistTimeout) {
        clearTimeout(this.persistTimeout);
      }

      if (this.rechargeErrorTimeout) {
        clearTimeout(this.rechargeErrorTimeout);
      }

      if (this._productQuantitySyncHandler) {
        document.removeEventListener(PRODUCT_QUANTITY_SYNC_EVENT, this._productQuantitySyncHandler);
      }

      if (this._productCardLoadingHandler) {
        document.removeEventListener(PRODUCT_CARD_LOADING_EVENT, this._productCardLoadingHandler);
      }

      if (activeRechargeCards.delete(this)) {
        this.broadcastLoadingState();
      }
    }

    validateExpiration() {
      if (!window.anchorDate || !window.portalLink) return;

      const anchorDateMs = new Date(window.anchorDate).getTime();
      const expirationDateMs = new Date(this.expirationDate).getTime();

      this.ctaWrappers.forEach((wrapper) => wrapper.classList.remove('opacity-50', 'pointer-events-none'));

      if (anchorDateMs < expirationDateMs) return;

      this.controls.forEach((controlGroup) => controlGroup.classList.add('hidden'));
      this.changeBillDateToggles.forEach((toggle) => toggle.classList.remove('hidden'));
    }

    handleProductQuantitySync(event) {
      if (!this.shouldUseRechargeFlow || !this.variantId) return;

      const detail = event?.detail || {};
      const eventType = detail?.type;
      const eventVariantId = detail?.variantId ? String(detail.variantId) : null;
      if (eventType !== this.type || eventVariantId !== String(this.variantId)) return;

      const nextQuantity = Math.max(0, Number(detail?.quantity) || 0);
      const nextId = detail?.id || null;
      const hasChanged = this.currentQuantity !== nextQuantity || String(this.itemId || '') !== String(nextId || '');
      if (!hasChanged) return;

      this.currentQuantity = nextQuantity;
      this.desiredQuantity = nextQuantity;
      this.lastConfirmedQuantity = nextQuantity;
      this.itemId = nextId;
      this.writeCachedProductQuantityToStorage(nextQuantity, nextId);
      this.applyUiState();
    }

    updateQuantity() {
      if (!this.quantityBadge) return;
      const quantity = this.currentQuantity || this.quantitySelectorInput?.value || 0;
      this.quantityBadge.innerText = quantity;
    }

    showQuantitySelector() {
      this.quantitySelectorWrappers.forEach((wrapper) => wrapper.classList.remove('hidden'));
      this.addToBoxButtons.forEach((button) => button.classList.add('hidden'));
      this.closeDetailsModal();
      this.classList.add('active');
      this.updateQuantity();
    }

    hideQuantitySelector() {
      this.quantitySelectorWrappers.forEach((wrapper) => wrapper.classList.add('hidden'));
      this.addToBoxButtons.forEach((button) => button.classList.remove('hidden'));
      this.classList.remove('active');
    }

    setLoading(isLoading) {
      this.isLoading = isLoading;

      if (isLoading) {
        activeRechargeCards.add(this);
      } else {
        activeRechargeCards.delete(this);
      }

      this.refreshControlsDisabledState();
      this.broadcastLoadingState();
    }

    refreshControlsDisabledState() {
      const disabled = this.isLoading || this.isRechargePending || this.isGloballyLocked;

      this.addToBoxButtons.forEach((button) => {
        button.disabled = disabled;
      });

      this.quantitySelectorWrappers.forEach((wrapper) => {
        wrapper.querySelector('quantity-selector')?.setControlsDisabled?.(disabled);
      });
    }

    broadcastLoadingState() {
      document.dispatchEvent(
        new CustomEvent(PRODUCT_CARD_LOADING_EVENT, {
          detail: { locked: activeRechargeCards.size > 0 },
        })
      );
    }

    handleProductCardLoading(event) {
      if (!this.shouldUseRechargeFlow) return;

      const locked = Boolean(event?.detail?.locked);
      if (this.isGloballyLocked === locked) return;

      this.isGloballyLocked = locked;
      this.refreshControlsDisabledState();
    }

    getRechargeErrorMount() {
      const visibleButton = this.addToBoxButtons.find((button) => !button.classList.contains('hidden'));
      if (visibleButton?.parentElement) return visibleButton.parentElement;

      const visibleSelector = this.quantitySelectorWrappers.find((wrapper) => !wrapper.classList.contains('hidden'));
      if (visibleSelector?.parentElement) return visibleSelector.parentElement;

      return this;
    }

    getRechargeErrorAnchor() {
      return (
        this.addToBoxButtons.find((button) => !button.classList.contains('hidden')) ||
        this.quantitySelectorWrappers.find((wrapper) => !wrapper.classList.contains('hidden')) ||
        this.addToBoxButtons[0] ||
        this.quantitySelectorWrappers[0] ||
        null
      );
    }

    syncRechargeErrorPlacement() {
      if (!this.rechargeErrorElement) return;

      const mount = this.getRechargeErrorMount();
      const anchor = this.getRechargeErrorAnchor();

      if (!mount) return;

      if (this.rechargeErrorElement.parentElement !== mount) {
        mount.appendChild(this.rechargeErrorElement);
      }

      if (anchor && anchor.parentElement === mount) {
        mount.insertBefore(this.rechargeErrorElement, anchor);
      } else if (mount.firstChild !== this.rechargeErrorElement) {
        mount.insertBefore(this.rechargeErrorElement, mount.firstChild);
      }
    }

    ensureRechargeErrorElement() {
      if (this.rechargeErrorElement && this.rechargeErrorElement.isConnected) {
        this.syncRechargeErrorPlacement();
        return this.rechargeErrorElement;
      }

      const mount = this.getRechargeErrorMount();
      const messageEl = document.createElement('p');
      messageEl.className = 'mb-2 text-sm text-system-red-500 hidden';
      messageEl.setAttribute('data-product-card-recharge-error', '');
      messageEl.setAttribute('role', 'status');
      messageEl.setAttribute('aria-live', 'polite');
      mount.appendChild(messageEl);
      this.rechargeErrorElement = messageEl;
      this.syncRechargeErrorPlacement();
      return messageEl;
    }

    normalizeRechargeErrorMessage(value) {
      if (!value) return '';

      if (Array.isArray(value)) {
        return value
          .map((entry) => this.normalizeRechargeErrorMessage(entry))
          .filter(Boolean)
          .join(' ');
      }

      if (typeof value === 'object') {
        return Object.values(value)
          .map((entry) => this.normalizeRechargeErrorMessage(entry))
          .filter(Boolean)
          .join(' ');
      }

      return String(value).trim();
    }

    extractRechargeErrorMessage(error) {
      const possibleObjects = [error?.errors, error?.response?.errors, error?.data?.errors, error?.body?.errors, error?.body];

      for (const obj of possibleObjects) {
        const message = this.normalizeRechargeErrorMessage(obj);
        if (message) return message;
      }

      const rawMessage = String(error?.message || '').trim();
      if (!rawMessage) return '';

      const jsonStart = rawMessage.indexOf('{');
      const jsonEnd = rawMessage.lastIndexOf('}');
      if (jsonStart !== -1 && jsonEnd > jsonStart) {
        const maybeJson = rawMessage.slice(jsonStart, jsonEnd + 1);
        try {
          const parsed = JSON.parse(maybeJson);
          const parsedMessage = this.normalizeRechargeErrorMessage(parsed);
          if (parsedMessage) return parsedMessage;
        } catch {
          // Use fallback message below if JSON parsing fails.
        }
      }

      return rawMessage;
    }

    showRechargeError(error) {
      const parsedMessage = this.extractRechargeErrorMessage(error);
      const message = parsedMessage || 'Unable to update this item right now.';
      const messageEl = this.ensureRechargeErrorElement();

      if (this.rechargeErrorTimeout) {
        clearTimeout(this.rechargeErrorTimeout);
      }

      messageEl.textContent = message;
      messageEl.classList.remove('hidden');
      this.syncRechargeErrorPlacement();
      this.rechargeErrorTimeout = setTimeout(() => {
        this.clearRechargeError();
      }, RECHARGE_ERROR_AUTO_HIDE_MS);
    }

    clearRechargeError() {
      if (this.rechargeErrorTimeout) {
        clearTimeout(this.rechargeErrorTimeout);
        this.rechargeErrorTimeout = null;
      }

      if (!this.rechargeErrorElement) return;
      this.rechargeErrorElement.textContent = '';
      this.rechargeErrorElement.classList.add('hidden');
    }

    applyUiState() {
      if (this.currentQuantity > 0 && this.quantitySelectorWrappers.length && this.addToBoxButtons.length) {
        this.quantitySelectorInputs.forEach((input) => {
          input.value = this.currentQuantity;
          input.closest('quantity-selector')?.updateButtonStates?.();
        });
        this.showQuantitySelector();
      } else if (this.quantitySelectorWrappers.length && this.addToBoxButtons.length) {
        this.hideQuantitySelector();
        this.quantitySelectorInputs.forEach((input) => {
          input.value = 1;
          input.closest('quantity-selector')?.updateButtonStates?.();
        });
      }

      this.updateQuantity();
      this.syncRechargeErrorPlacement();
    }

    async syncInitialState() {
      const api = await this.waitForRechargeApi();
      if (!api) return;

      try {
        const cachedBeforeSync =
          this.getCachedProductQuantityFromStorage() || api.getCachedProductQuantity?.(this.type, this.variantId) || null;

        const item = await api.getProductQuantity({
          type: this.type,
          variantId: this.variantId,
        });

        const normalizedRemote = {
          quantity: Number(item?.quantity) || 0,
          id: item?.id || null,
        };

        if (!this.isSameQuantitySnapshot(cachedBeforeSync, normalizedRemote)) {
          this.writeCachedProductQuantityToStorage(normalizedRemote.quantity, normalizedRemote.id);
        }

        this.currentQuantity = item.quantity || 0;
        this.desiredQuantity = this.currentQuantity;
        this.lastConfirmedQuantity = this.currentQuantity;
        this.itemId = item.id || null;
        this.applyUiState();
      } catch (error) {
        const isConnectionIssue =
          error?.name === 'RechargeRequestError' &&
          error?.status === undefined &&
          String(error?.message || '')
            .toLowerCase()
            .includes('connection error');

        if (!isConnectionIssue) {
          console.error('Failed to sync product card state from Recharge:', error);
        }
      }
    }

    queueRechargeQuantityUpdate(quantity) {
      const rawQuantity = Math.max(0, Number(quantity) || 0);
      const parsedQuantity = Math.min(rawQuantity, this.maxQuantity);
      this.clearRechargeError();
      this.desiredQuantity = parsedQuantity;
      this.currentQuantity = parsedQuantity;
      this.applyUiState();
      this.writeCachedProductQuantityToStorage(parsedQuantity);

      if (this.persistTimeout) {
        clearTimeout(this.persistTimeout);
      }

      this.persistTimeout = setTimeout(() => {
        this.flushQueuedRechargeQuantityUpdate();
      }, 150);
    }

    async flushQueuedRechargeQuantityUpdate() {
      if (!this.shouldUseRechargeFlow || this.isPersistingQuantity) return;

      const targetQuantity = Math.max(0, Number(this.desiredQuantity) || 0);
      if (targetQuantity === this.lastConfirmedQuantity) return;

      this.isPersistingQuantity = true;
      try {
        await this.updateRechargeQuantity(targetQuantity);
      } finally {
        this.isPersistingQuantity = false;

        if (this.desiredQuantity !== this.lastConfirmedQuantity) {
          this.flushQueuedRechargeQuantityUpdate();
        }
      }
    }

    async updateRechargeQuantity(quantity) {
      if (!this.shouldUseRechargeFlow) return;

      const api = await this.waitForRechargeApi();
      if (!api) return;

      const previousConfirmedQuantity = this.lastConfirmedQuantity;
      this.setLoading(true);

      try {
        const updated = await api.setProductQuantity({
          type: this.type,
          variantId: this.variantId,
          productId: this.productId,
          itemId: this.itemId,
          quantity,
          properties: {
            _type: this.type,
          },
        });

        this.currentQuantity = updated.quantity || 0;
        this.desiredQuantity = this.currentQuantity;
        this.lastConfirmedQuantity = this.currentQuantity;
        this.itemId = updated.id || null;
        this.writeCachedProductQuantityToStorage(this.currentQuantity, this.itemId);
        this.applyUiState();
        this.clearRechargeError();
      } catch (error) {
        console.error('Failed to update Recharge purchase item:', error);

        const isRateLimited = error?.status === 429;
        if (isRateLimited) {
          setTimeout(() => this.flushQueuedRechargeQuantityUpdate(), 1500);
          return;
        }

        if (this.desiredQuantity === quantity) {
          this.currentQuantity = previousConfirmedQuantity;
          this.desiredQuantity = previousConfirmedQuantity;
          this.applyUiState();
          this.writeCachedProductQuantityToStorage(previousConfirmedQuantity);
        }

        this.showRechargeError(error);
      } finally {
        this.setLoading(false);
      }
    }

    handleAddToBoxClick() {
      if (this.isRechargePending) return;
      if (this.shouldUseRechargeFlow && (this.isLoading || this.isGloballyLocked)) return;

      if (this.shouldUseRechargeFlow) {
        this.queueRechargeQuantityUpdate(Math.max(1, this.currentQuantity || 0));
        return;
      }

      this.showQuantitySelector();
    }

    handleRemoveFromBox() {
      if (this.isRechargePending) return;
      if (this.shouldUseRechargeFlow && (this.isLoading || this.isGloballyLocked)) return;

      if (this.shouldUseRechargeFlow) {
        this.queueRechargeQuantityUpdate(0);
        return;
      }

      this.hideQuantitySelector();
    }

    handleQuantityChange(event) {
      if (this.isRechargePending) return;
      if (this.shouldUseRechargeFlow && (this.isLoading || this.isGloballyLocked)) return;

      const activeInput = event?.currentTarget || this.quantitySelectorInput;
      if (!activeInput) return;
      const quantity = parseInt(activeInput.value, 10) || 0;

      if (this.shouldUseRechargeFlow) {
        this.queueRechargeQuantityUpdate(quantity);
        return;
      }

      this.currentQuantity = quantity;
      this.updateQuantity();
    }

    openDetailsModal(triggerElement) {
      if (!this.detailsModal) return;
      this._previouslyFocused = triggerElement || document.activeElement;
      this.detailsModal.classList.add('is-open');
      this.detailsModal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      this.detailsModal.querySelector('[data-product-details-modal-close]')?.focus();
      window.trapFocus(this.detailsModal);
    }

    closeDetailsModal() {
      if (!this.detailsModal) return;
      this.detailsModal.classList.remove('is-open');
      this.detailsModal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      this._previouslyFocused?.focus();
      window.removeTrapFocus();
    }
  }

  window.customElements.define('product-card', ProductCard);
}
